import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  consignmentStatementBlock,
  type ConsignmentSaleAllocation,
  type ConsignmentSaleEvent,
} from '@tcvs/shared';
import { ConsignmentFeeBreakdown } from './ConsignmentFeeBreakdown';
import { useConsignmentApi } from './consignment-api';
import { consignmentErrorMessage, formatMoneyMinor } from './consignment-ui';

const states: Record<string, string> = {
  PROVISIONAL: 'Provisional',
  PARTIALLY_REFUNDED: 'Partially refunded',
  REVERSED: 'Reversed',
  REVIEW_REQUIRED: 'Needs review',
  SETTLED: 'Settled',
  APPROVED: 'Reserved in approved statement',
  DRAFT: 'Reserved in draft',
};
const reasons: Record<string, string> = {
  SOURCE_TIMESTAMP_MISSING:
    'eBay modification time is missing. Reconcile this order before settlement.',
  SOURCE_VERSION_CONFLICT:
    'eBay returned conflicting details for the same update. Reconcile the latest order.',
  CANCELLATION_PENDING: 'Cancellation requested; awaiting eBay confirmation.',
  CANCELLATION_STATUS_UNKNOWN: 'Unrecognized cancellation status. Check this order in eBay.',
  PAYMENT_STATUS_REQUIRES_REVIEW: 'Payment is not confirmed. Check this order in eBay.',
  REFUND_DETAILS_INVALID:
    'Refund ID, amount, or USD currency is missing or invalid. Check the refund in eBay.',
  REFUND_ID_CONFLICT: 'The same refund ID has conflicting details. Check the refund in eBay.',
  REFUND_PENDING: 'Refund has not been confirmed as issued. Await the next order synchronization.',
  REFUND_FAILED: 'Refund failed. Check the refund in eBay.',
  REFUND_STATUS_UNKNOWN: 'Unrecognized refund status. Check the refund in eBay.',
  REFUND_DETAILS_MISSING:
    'eBay reports a partial refund without card-level amounts. Reconcile before settlement.',
  ORDER_REFUND_NEEDS_ALLOCATION:
    'Order-level refund cannot be assigned to this card automatically. Reconcile item and shipping amounts before settlement.',
  REFUND_EXCEEDS_ITEM_AMOUNT:
    'Refund exceeds the original item amount; shipping or other amounts may be included. Reconcile before settlement.',
  REFUND_EVIDENCE_REGRESSED:
    'Previously observed refund details are missing or lower. Prior deductions remain protected; reconcile before settlement.',
  REFUND_EVIDENCE_LIMIT:
    'Refund history exceeds the automatic processing limit. Review this order manually.',
  ADJUSTMENT_AFTER_SETTLEMENT:
    'A settled sale changed. Review a separate settlement adjustment; do not pay again.',
  ADJUSTMENT_AFTER_APPROVAL:
    'An approved statement sale changed. Review a separate adjustment before any payment; the approved snapshot is unchanged.',
};

export function ConsignmentLedger({
  ledger,
  selection,
}: {
  ledger: ConsignmentSaleAllocation[];
  selection?: { ids: string[]; change: (id: string, checked: boolean) => void; disabled: boolean };
}): JSX.Element {
  const [filter, setFilter] = useState('ALL');
  const state = (item: ConsignmentSaleAllocation): string =>
    item.lifecycle?.state === 'REVIEW_REQUIRED'
      ? 'REVIEW_REQUIRED'
      : item.status === 'SETTLED'
        ? 'SETTLED'
        : (item.statement?.status ?? item.lifecycle?.state ?? item.status);
  const rows = ledger.filter((item) => filter === 'ALL' || state(item) === filter);
  const reviewCount = ledger.filter((item) => state(item) === 'REVIEW_REQUIRED').length;
  const proceeds = ledger
    .filter(
      (item) =>
        state(item) !== 'REVIEW_REQUIRED' &&
        item.status !== 'SETTLED' &&
        item.statement?.status !== 'APPROVED',
    )
    .reduce(
      (total, item) =>
        total +
        (item.lifecycle?.estimatedProceedsMinor ??
          (item.status === 'REVERSED' ? 0 : item.estimatedProceedsMinor)),
      0,
    );
  return (
    <>
      <dl className="tcvs-detail-list">
        <div>
          <dt>Loaded sales</dt>
          <dd>{ledger.length}</dd>
        </div>
        <div>
          <dt>Needs review</dt>
          <dd>{reviewCount}</dd>
        </div>
        <div>
          <dt>Unapproved estimated proceeds (excluding review holds)</dt>
          <dd>{formatMoneyMinor(proceeds)}</dd>
        </div>
      </dl>
      <label>
        Status{' '}
        <select
          aria-label="Ledger status"
          value={filter}
          onChange={(event) => setFilter(event.target.value)}
        >
          <option value="ALL">All</option>
          {Object.entries(states).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </label>
      {rows.length === 0 ? (
        <p>No matching sales.</p>
      ) : (
        <div className="tcvs-table-wrap">
          <table className="tcvs-table">
            <thead>
              <tr>
                {selection && <th>Select</th>}
                <th>Sold</th>
                <th>Card</th>
                <th>eBay item</th>
                <th>Original gross</th>
                <th>Item refund applied</th>
                <th>Estimated proceeds</th>
                <th>Hold until</th>
                <th>Status</th>
                <th>History</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((item) => (
                <tr key={item.allocationId}>
                  {selection && (
                    <td>
                      <input
                        type="checkbox"
                        aria-label={`Select sale ${item.cardId.slice(-8)}`}
                        checked={selection.ids.includes(item.allocationId)}
                        disabled={
                          selection.disabled ||
                          (!selection.ids.includes(item.allocationId) &&
                            selection.ids.length >= 50) ||
                          Boolean(consignmentStatementBlock(item, new Date().toISOString()))
                        }
                        onChange={(e) => selection.change(item.allocationId, e.target.checked)}
                      />
                      <span className="tcvs-statement-readiness">
                        {consignmentStatementBlock(item, new Date().toISOString()) ?? 'Eligible'}
                      </span>
                    </td>
                  )}
                  <td>{item.soldAt.slice(0, 10)}</td>
                  <td>
                    <Link to={`/catalog/${item.cardId}`}>{item.cardId.slice(-8)}</Link>
                  </td>
                  <td>
                    {item.externalListingId ? (
                      <a
                        href={`https://www.ebay.com/itm/${encodeURIComponent(item.externalListingId)}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        {item.externalListingId}
                      </a>
                    ) : (
                      'Not provided'
                    )}
                  </td>
                  <td>{formatMoneyMinor(item.grossAmountMinor)}</td>
                  <td>{formatMoneyMinor(item.lifecycle?.refundAmountMinor ?? 0)}</td>
                  <td>
                    {state(item) === 'REVIEW_REQUIRED'
                      ? 'On review hold'
                      : formatMoneyMinor(
                          item.lifecycle?.estimatedProceedsMinor ??
                            (item.status === 'REVERSED' ? 0 : item.estimatedProceedsMinor),
                        )}
                  </td>
                  <td>
                    {['REVERSED', 'REVIEW_REQUIRED'].includes(state(item))
                      ? 'Blocked'
                      : item.payoutEligibleAt.slice(0, 10)}
                  </td>
                  <td>
                    {states[state(item)] ?? state(item)}
                    {item.lifecycle?.reversalReason === 'CANCELED' ? ' (canceled)' : ''}
                  </td>
                  <td>
                    <LedgerHistory
                      key={`${item.allocationId}:${item.lifecycle?.version ?? 0}`}
                      item={item}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

function LedgerHistory({ item }: { item: ConsignmentSaleAllocation }): JSX.Element {
  const api = useConsignmentApi();
  const [events, setEvents] = useState<ConsignmentSaleEvent[]>();
  const [error, setError] = useState<string>();
  const [loading, setLoading] = useState(false);
  async function load(): Promise<void> {
    if (loading) return;
    setLoading(true);
    setError(undefined);
    try {
      const result = await api.listLedgerEvents(item.consignorId, item.allocationId);
      if (result.ok) setEvents(result.data.items);
      else setError(consignmentErrorMessage(result));
    } catch {
      setError('Could not load sale history. Close and reopen to retry.');
    } finally {
      setLoading(false);
    }
  }
  return (
    <details
      style={{ minWidth: 180, maxWidth: 340, overflowWrap: 'anywhere' }}
      onToggle={(event) => {
        if (event.currentTarget.open && events === undefined) void load();
      }}
    >
      <summary aria-label={`Sale history ${item.cardId.slice(-8)}`}>Details</summary>
      <ConsignmentFeeBreakdown
        calculation={item.lifecycle?.feeCalculation ?? item.feeCalculation}
      />
      <dl>
        <dt>Original commission / fixed fee</dt>
        <dd>
          {formatMoneyMinor(item.commissionAmountMinor)} / {formatMoneyMinor(item.fixedFeeMinor)}
        </dd>
        <dt>Original estimated proceeds</dt>
        <dd>{formatMoneyMinor(item.estimatedProceedsMinor)}</dd>
        {item.lifecycle && (
          <>
            <dt>Adjusted commission / fixed fee</dt>
            <dd>
              {formatMoneyMinor(item.lifecycle.commissionAmountMinor)} /{' '}
              {formatMoneyMinor(item.lifecycle.fixedFeeMinor)}
            </dd>
          </>
        )}
      </dl>
      {item.lifecycle?.reviewReasons.map((reason) => (
        <p key={reason} role="note">
          {reasons[reason] ?? reason}
        </p>
      ))}
      {loading && <p role="status">Loading history...</p>}
      {error && <p role="alert">{error}</p>}
      {events && (
        <>
          <h3>Latest 25 changes</h3>
          {events.length === 0 ? (
            <p>No lifecycle changes recorded.</p>
          ) : (
            <ol>
              {events.map((event) => (
                <li key={event.eventId}>
                  {event.lifecycle.observedAt.replace('T', ' ').slice(0, 19)} UTC:{' '}
                  {states[event.lifecycle.state]}. Proceeds adjustment{' '}
                  {event.proceedsAdjustmentMinor < 0 ? '-' : ''}
                  {formatMoneyMinor(Math.abs(event.proceedsAdjustmentMinor))}.
                </li>
              ))}
            </ol>
          )}
        </>
      )}
    </details>
  );
}
